import React, { memo, useCallback, useEffect } from 'react';
import { Paragraph4 } from '@nexus/lib-react/dist/core/Typography';
import S from '../style/Dac7TabContent';
import Dac7Dropdown from './Dac7Dropdown';
import countries from '../utils/countriesList';
import Dac7Input from './Dac7Input';
import V from '../utils/validation';
import { TaxDetails } from '../../../../../../typings/dac7';
import { useI18n } from '../../../../../i18n';
import useDac7ArrayItems from '../hooks/useDac7ArrayItems';
import Dac7RemoveItemButton from './Dac7RemoveItemButton';
import Dac7AddItemButton from './Dac7AddItemButton';

interface Props {
  taxDetails: Readonly<TaxDetails['taxNumbersDetails']>;
  frozenTaxDetails: Readonly<TaxDetails['taxNumbersDetails']>;
  isPrivate?: boolean;
  onAdd?: () => void;
  onRemove?: (index: number) => void;
  onChange?: (index: number, value: string) => void;
}

const Dac7TaxDetails = ({
  taxDetails: taxDetailsInitial,
  frozenTaxDetails,
  isPrivate,
  onAdd,
  onRemove,
  onChange,
}: Props) => {
  const { items: taxDetails, addItem, removeItem } = useDac7ArrayItems(taxDetailsInitial);
  const i18n = useI18n();

  const addTaxDetail = useCallback(() => {
    addItem({ taxId: '', countryCode: '' });
    onAdd?.();
  }, []);

  const removeTaxDetail = useCallback(i => {
    removeItem(i);
    onRemove?.(i);
  }, []);

  useEffect(() => {
    if (taxDetailsInitial.length === 0) {
      addTaxDetail();
    }
  }, []);

  return (
    <>
      {taxDetails.map((taxDetail, i) => (
        <S.Dac7Fieldset
          className="dac7-tax-details"
          key={`dac7_taxDetails_${taxDetail?.feId || i}`}
        >
          <S.Dac7FormField>
            <Dac7Dropdown
              sourceItems={countries}
              initialSelectedIds={[taxDetail.countryCode]}
              name={`dac7_taxDetails.taxNumbersDetails.${i}.countryCode`}
              label={`${i18n('dlv_dac7_taxDetails_taxIdIssuerCountry')}${
                i === 0 ? '' : ` #${i + 1}`
              }`}
              required
              disabled={!!frozenTaxDetails[i]?.countryCode}
              requiredMessage={i18n('dlv_dac7_taxDetails_taxIdIssuerCountryRequired')}
              placeholder={i18n('dlv_dac7_taxDetails_taxIdIssuerCountryPlaceholder')}
              onChange={([countryCode]) => {
                onChange?.(i, String(countryCode));
              }}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7Input
              name={`dac7_taxDetails.taxNumbersDetails.${i}.taxId`}
              label={
                i === 0 && isPrivate
                  ? `${i18n('dlv_dac7_idNumber')}${i === 0 ? '' : ` #${i + 1}`}`
                  : `${i18n('dlv_dac7_taxDetails_taxId')}${i === 0 ? '' : ` #${i + 1}`}`
              }
              pattern={V.getMinCharsPattern(2)}
              required
              value={taxDetail.taxId}
              disabled={!!frozenTaxDetails[i]?.taxId}
            />
            {i === 0 && isPrivate ? (
              <Paragraph4 mt="4px" color="text-global-secondary">
                {i18n('dlv_dac7_pesel_info')}
              </Paragraph4>
            ) : null}
          </S.Dac7FormField>
          {i === 0 || !!taxDetail.taxId ? null : (
            <Dac7RemoveItemButton
              onClick={() => removeTaxDetail(i)}
              label={i18n('dlv_dac7_remove_item_label')}
            />
          )}
        </S.Dac7Fieldset>
      ))}
      <Dac7AddItemButton onClick={addTaxDetail} label={i18n('dlv_dac7_add_tax_details_label')} />
    </>
  );
};

export default memo(Dac7TaxDetails);
